import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import {
  AdminApiError,
  updateAdminRequestType,
  type DepartmentAdminResponse,
  type RequestTypeAdminResponse,
  type RequestTypeUpdatePayload,
} from "../../api/admin";
import "./RequestCategoryEditPanel.css";

export type RequestCategoryEditPanelProps = {
  requestType: RequestTypeAdminResponse;
  departments: ReadonlyArray<DepartmentAdminResponse>;
  disabled: boolean;
  onUpdated: () => Promise<void>;
  onCancel: () => void;
};

type FieldErrors = {
  name: string;
  sortOrder: string;
};

type FormState = {
  name: string;
  description: string;
  sortOrder: string;
  defaultDepartmentId: number | null;
  isInternalVisible: boolean;
  isCustomerVisible: boolean;
  isPartnerVisible: boolean;
};

type UiState = {
  isSubmitting: boolean;
  fieldErrors: FieldErrors;
  formError: string;
  noChange: string;
};

const EMPTY_FIELD_ERRORS: FieldErrors = {
  name: "",
  sortOrder: "",
};

function initialUi(): UiState {
  return {
    isSubmitting: false,
    fieldErrors: { ...EMPTY_FIELD_ERRORS },
    formError: "",
    noChange: "",
  };
}

function buildFormFromRequestType(
  requestType: RequestTypeAdminResponse,
): FormState {
  return {
    name: requestType.name,
    description: requestType.description ?? "",
    sortOrder: String(requestType.sort_order),
    defaultDepartmentId: requestType.default_department_id,
    isInternalVisible: requestType.is_internal_visible,
    isCustomerVisible: requestType.is_customer_visible,
    isPartnerVisible: requestType.is_partner_visible,
  };
}

function resolveFieldNameFromLocation(
  location: ReadonlyArray<string | number>,
): string | null {
  for (let index = location.length - 1; index >= 0; index -= 1) {
    const segment = location[index];
    if (typeof segment === "string" && segment.length > 0) {
      return segment;
    }
  }
  return null;
}

function describeRoutingDepartment(
  department: DepartmentAdminResponse,
): string {
  const companyName = department.company_name ?? "Unassigned";
  return companyName + " - " + department.name;
}

function normalizeString(value: string): string {
  return value.trim();
}

export default function RequestCategoryEditPanel(
  props: RequestCategoryEditPanelProps,
) {
  const { requestType, departments, disabled, onUpdated, onCancel } = props;
  const [form, setForm] = useState<FormState>(() =>
    buildFormFromRequestType(requestType),
  );
  const [ui, setUi] = useState<UiState>(initialUi);
  const headingId = useId();
  const codeContextId = useId();
  const nameErrorId = useId();
  const sortErrorId = useId();
  const audienceErrorId = useId();
  const formErrorId = useId();
  const noChangeId = useId();
  const statusHintId = useId();
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const isMountedRef = useRef(true);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    headingRef.current?.focus();
  }, [requestType.id]);

  const eligibleDepartments = departments.filter(
    (department) =>
      department.is_active && department.is_ticket_routing_enabled,
  );

  const clearNotifications = useCallback(() => {
    setUi((prev) => ({
      ...prev,
      fieldErrors: { ...EMPTY_FIELD_ERRORS },
      formError: "",
      noChange: "",
    }));
  }, []);

  const buildClientFieldErrors = useCallback((): FieldErrors => {
    const trimmedName = form.name.trim();
    const trimmedSort = form.sortOrder.trim();
    const sortAsNumber =
      trimmedSort.length === 0 ? Number.NaN : Number(trimmedSort);
    const sortIsValidInteger =
      trimmedSort.length > 0 &&
      Number.isFinite(sortAsNumber) &&
      Number.isInteger(sortAsNumber) &&
      sortAsNumber >= 0;
    const errors: FieldErrors = { ...EMPTY_FIELD_ERRORS };
    if (trimmedName.length === 0) {
      errors.name = "Category name is required.";
    }
    if (!sortIsValidInteger) {
      errors.sortOrder = "Sort order must be a non-negative whole number.";
    }
    return errors;
  }, [form.name, form.sortOrder]);

  const hasAudienceSelected =
    form.isInternalVisible ||
    form.isCustomerVisible ||
    form.isPartnerVisible;

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setForm((prev) => ({ ...prev, name: nextValue }));
      setUi((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, name: "" },
        formError: "",
        noChange: "",
      }));
    },
    [],
  );

  const handleDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const nextValue = event.target.value;
      setForm((prev) => ({ ...prev, description: nextValue }));
      setUi((prev) => ({
        ...prev,
        formError: "",
        noChange: "",
      }));
    },
    [],
  );

  const handleSortOrderChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setForm((prev) => ({ ...prev, sortOrder: nextValue }));
      setUi((prev) => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, sortOrder: "" },
        formError: "",
        noChange: "",
      }));
    },
    [],
  );

  const handleDepartmentChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const rawValue = event.target.value;
      const parsed = rawValue === "" ? null : Number.parseInt(rawValue, 10);
      const nextValue =
        parsed !== null && Number.isFinite(parsed) ? parsed : null;
      setForm((prev) => ({ ...prev, defaultDepartmentId: nextValue }));
      setUi((prev) => ({ ...prev, formError: "", noChange: "" }));
    },
    [],
  );

  const handleInternalToggle = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      isInternalVisible: !prev.isInternalVisible,
    }));
    setUi((prev) => ({ ...prev, formError: "", noChange: "" }));
  }, []);

  const handleCustomerToggle = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      isCustomerVisible: !prev.isCustomerVisible,
    }));
    setUi((prev) => ({ ...prev, formError: "", noChange: "" }));
  }, []);

  const handlePartnerToggle = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      isPartnerVisible: !prev.isPartnerVisible,
    }));
    setUi((prev) => ({ ...prev, formError: "", noChange: "" }));
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitInFlightRef.current || disabled) {
        return;
      }
      clearNotifications();

      const clientErrors = buildClientFieldErrors();
      const hasClientErrors =
        clientErrors.name.length > 0 || clientErrors.sortOrder.length > 0;
      if (hasClientErrors) {
        setUi((prev) => ({ ...prev, fieldErrors: clientErrors }));
        return;
      }

      const trimmedName = normalizeString(form.name);
      const trimmedDescription = normalizeString(form.description);
      const sortAsNumber = Number(form.sortOrder.trim());

      const originalDepartmentId = requestType.default_department_id;
      const trimmedOriginalDescription = (requestType.description ?? "").trim();

      const nameChanged = trimmedName !== requestType.name;
      const descriptionChanged =
        trimmedDescription !== trimmedOriginalDescription;
      const sortChanged = sortAsNumber !== requestType.sort_order;
      const departmentChanged =
        form.defaultDepartmentId !== originalDepartmentId;
      const audienceChanged =
        form.isInternalVisible !== requestType.is_internal_visible ||
        form.isCustomerVisible !== requestType.is_customer_visible ||
        form.isPartnerVisible !== requestType.is_partner_visible;

      if (
        !nameChanged &&
        !descriptionChanged &&
        !sortChanged &&
        !departmentChanged &&
        !audienceChanged
      ) {
        setUi((prev) => ({
          ...prev,
          noChange: "No changes to save.",
        }));
        return;
      }

      if (requestType.is_active) {
        if (!hasAudienceSelected) {
          setUi((prev) => ({
            ...prev,
            formError:
              "At least one audience flag must remain enabled for an active category.",
          }));
          return;
        }
        if (form.defaultDepartmentId === null) {
          setUi((prev) => ({
            ...prev,
            formError:
              "An active category requires a routing-enabled Internal-company Department.",
          }));
          return;
        }
      }

      const payload: RequestTypeUpdatePayload = {};
      if (nameChanged) {
        payload.name = trimmedName;
      }
      if (descriptionChanged) {
        payload.description =
          trimmedDescription.length === 0 ? null : trimmedDescription;
      }
      if (sortChanged) {
        payload.sort_order = sortAsNumber;
      }
      if (departmentChanged) {
        payload.default_department_id = form.defaultDepartmentId;
      }
      if (audienceChanged) {
        payload.is_internal_visible = form.isInternalVisible;
        payload.is_customer_visible = form.isCustomerVisible;
        payload.is_partner_visible = form.isPartnerVisible;
      }

      submitInFlightRef.current = true;
      setUi((prev) => ({ ...prev, isSubmitting: true }));

      try {
        await updateAdminRequestType(requestType.id, payload);
        if (!isMountedRef.current) {
          return;
        }
        setUi((prev) => ({
          ...prev,
          isSubmitting: false,
        }));
        await onUpdated();
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        if (error instanceof AdminApiError) {
          const fieldErrors: FieldErrors = { ...EMPTY_FIELD_ERRORS };
          let hasRecognisedFieldError = false;
          let hasUnrecognisedFieldError = false;
          for (const fieldError of error.fieldErrors) {
            const fieldName = resolveFieldNameFromLocation(fieldError.location);
            if (fieldName === null) {
              hasUnrecognisedFieldError = true;
              continue;
            }
            if (fieldName === "name") {
              fieldErrors.name = fieldError.message;
              hasRecognisedFieldError = true;
            } else if (fieldName === "sort_order") {
              fieldErrors.sortOrder = fieldError.message;
              hasRecognisedFieldError = true;
            } else {
              hasUnrecognisedFieldError = true;
            }
          }
          if (error.errorCode === "DUPLICATE_REQUEST_TYPE_NAME") {
            fieldErrors.name = "Request category name already exists.";
            hasRecognisedFieldError = true;
            hasUnrecognisedFieldError = false;
          }
          const showDetail =
            !hasRecognisedFieldError || hasUnrecognisedFieldError;
          setUi((prev) => ({
            ...prev,
            isSubmitting: false,
            fieldErrors,
            formError: showDetail ? error.detail : "",
          }));
          return;
        }
        setUi((prev) => ({
          ...prev,
          isSubmitting: false,
          formError:
            "Unable to update the request category. Please try again.",
        }));
      } finally {
        if (isMountedRef.current) {
          submitInFlightRef.current = false;
        }
      }
    },
    [
      buildClientFieldErrors,
      clearNotifications,
      disabled,
      form.defaultDepartmentId,
      form.description,
      form.isCustomerVisible,
      form.isInternalVisible,
      form.isPartnerVisible,
      form.name,
      form.sortOrder,
      hasAudienceSelected,
      onUpdated,
      requestType.default_department_id,
      requestType.description,
      requestType.id,
      requestType.is_active,
      requestType.is_customer_visible,
      requestType.is_internal_visible,
      requestType.is_partner_visible,
      requestType.name,
      requestType.sort_order,
    ],
  );

  const allInputsDisabled = disabled || ui.isSubmitting;

  const statusHelpText = requestType.is_active
    ? "Saving changes applies them server-side. Active categories require a routing-enabled Internal-company Department and at least one audience flag."
    : "Saving changes applies them server-side. Inactive categories may retain or repair their audience and routing configuration.";

  return (
    <section
      className="request-category-edit-panel"
      aria-labelledby={headingId}
      id={`request-category-edit-panel-${requestType.id}`}
    >
      <h2
        id={headingId}
        ref={headingRef}
        className="request-category-edit-panel__title"
        tabIndex={-1}
      >
        Edit Request Category
      </h2>

      <dl className="request-category-edit-panel__context">
        <div className="request-category-edit-panel__context-row">
          <dt className="request-category-edit-panel__context-label">
            Category code
          </dt>
          <dd
            id={codeContextId}
            className="request-category-edit-panel__context-value"
          >
            {requestType.code}
          </dd>
        </div>
      </dl>

      <form
        noValidate
        className="request-category-edit-panel__form"
        onSubmit={handleSubmit}
        aria-describedby={statusHintId}
      >
        <p className="request-category-edit-panel__hint" id={statusHintId}>
          {statusHelpText}
        </p>

        <div className="request-category-edit-panel__field">
          <label
            htmlFor="request-category-edit-name"
            className="request-category-edit-panel__label"
          >
            Category name
          </label>
          <input
            id="request-category-edit-name"
            className="request-category-edit-panel__input"
            type="text"
            value={form.name}
            onChange={handleNameChange}
            disabled={allInputsDisabled}
            required
            aria-required="true"
            aria-invalid={ui.fieldErrors.name.length > 0}
            aria-describedby={
              ui.fieldErrors.name.length > 0 ? nameErrorId : undefined
            }
          />
          {ui.fieldErrors.name.length > 0 ? (
            <p
              id={nameErrorId}
              className="request-category-edit-panel__field-error"
            >
              {ui.fieldErrors.name}
            </p>
          ) : null}
        </div>

        <div className="request-category-edit-panel__field">
          <label
            htmlFor="request-category-edit-description"
            className="request-category-edit-panel__label"
          >
            Description
          </label>
          <textarea
            id="request-category-edit-description"
            className="request-category-edit-panel__textarea"
            value={form.description}
            onChange={handleDescriptionChange}
            disabled={allInputsDisabled}
            rows={3}
          />
        </div>

        <div className="request-category-edit-panel__field">
          <label
            htmlFor="request-category-edit-sort-order"
            className="request-category-edit-panel__label"
          >
            Sort order
          </label>
          <input
            id="request-category-edit-sort-order"
            className="request-category-edit-panel__input"
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={form.sortOrder}
            onChange={handleSortOrderChange}
            disabled={allInputsDisabled}
            required
            aria-required="true"
            aria-invalid={ui.fieldErrors.sortOrder.length > 0}
            aria-describedby={
              ui.fieldErrors.sortOrder.length > 0 ? sortErrorId : undefined
            }
          />
          {ui.fieldErrors.sortOrder.length > 0 ? (
            <p
              id={sortErrorId}
              className="request-category-edit-panel__field-error"
            >
              {ui.fieldErrors.sortOrder}
            </p>
          ) : null}
        </div>

        <div className="request-category-edit-panel__field">
          <label
            htmlFor="request-category-edit-default-department"
            className="request-category-edit-panel__label"
          >
            Default routing Department
          </label>
          <select
            id="request-category-edit-default-department"
            className="request-category-edit-panel__select"
            value={
              form.defaultDepartmentId === null
                ? ""
                : String(form.defaultDepartmentId)
            }
            onChange={handleDepartmentChange}
            disabled={allInputsDisabled}
          >
            <option value="">Not set</option>
            {eligibleDepartments.map((department) => (
              <option key={department.id} value={department.id}>
                {describeRoutingDepartment(department)}
              </option>
            ))}
          </select>
          <p className="request-category-edit-panel__hint">
            Only active, routing-enabled Departments owned by an Internal
            company are eligible.
          </p>
        </div>

        <fieldset
          className="request-category-edit-panel__audience"
          aria-describedby={
            !hasAudienceSelected && requestType.is_active
              ? audienceErrorId
              : undefined
          }
        >
          <legend className="request-category-edit-panel__label">
            Audience visibility
          </legend>
          <label className="request-category-edit-panel__audience-option">
            <input
              type="checkbox"
              checked={form.isInternalVisible}
              onChange={handleInternalToggle}
              disabled={allInputsDisabled}
            />
            <span>Internal requesters</span>
          </label>
          <label className="request-category-edit-panel__audience-option">
            <input
              type="checkbox"
              checked={form.isCustomerVisible}
              onChange={handleCustomerToggle}
              disabled={allInputsDisabled}
            />
            <span>Customer requesters</span>
          </label>
          <label className="request-category-edit-panel__audience-option">
            <input
              type="checkbox"
              checked={form.isPartnerVisible}
              onChange={handlePartnerToggle}
              disabled={allInputsDisabled}
            />
            <span>Partner requesters</span>
          </label>
          {!hasAudienceSelected && requestType.is_active ? (
            <p
              id={audienceErrorId}
              className="request-category-edit-panel__field-error"
              role="alert"
            >
              At least one audience flag must remain enabled for an active category.
            </p>
          ) : null}
          {!hasAudienceSelected && !requestType.is_active ? (
            <p
              className="request-category-edit-panel__hint"
              id={audienceErrorId}
            >
              Inactive categories may have no audience flags. Reactivation will be
              blocked until at least one audience flag is enabled.
            </p>
          ) : null}
        </fieldset>

        {ui.formError.length > 0 ? (
          <p
            id={formErrorId}
            className="request-category-edit-panel__form-error"
            role="alert"
          >
            {ui.formError}
          </p>
        ) : null}

        {ui.noChange.length > 0 ? (
          <p
            id={noChangeId}
            className="request-category-edit-panel__no-change"
            aria-live="polite"
            role="status"
          >
            {ui.noChange}
          </p>
        ) : null}

        <div
          className="request-category-edit-panel__actions"
          role="group"
          aria-labelledby={headingId}
        >
          <button
            type="submit"
            className="request-category-edit-panel__submit"
            disabled={allInputsDisabled}
            aria-busy={ui.isSubmitting}
          >
            {ui.isSubmitting ? "Saving…" : "Save changes"}
          </button>
          <button
            type="button"
            className="request-category-edit-panel__cancel"
            onClick={onCancel}
            disabled={ui.isSubmitting}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}
